import axios from "axios";
import { clearSession, loadSession, saveSession } from "../store";
import { getRefreshToken } from "./authentication";
import { apiRoutes } from "../types/api-routes";

const service = axios.create();

/** Single-flight refresh so concurrent 401s share one token rotation. */
let refreshInFlight = null;

function redirectToSignIn() {
  clearSession();
  window.location.assign("/signin");
}

/**
 * Exchange refresh token for new tokens and persist session. Throws if refresh fails.
 * Used by the response interceptor and by layout when the access JWT is expired.
 */
export async function refreshSessionTokens() {
  const session = loadSession();
  if (!session?.refresh_token) {
    throw new Error("Missing refresh token");
  }

  if (!refreshInFlight) {
    refreshInFlight = getRefreshToken({
      refreshToken: session.refresh_token,
    }).finally(() => {
      refreshInFlight = null;
    });
  }

  const res = await refreshInFlight;
  const refreshedInfo = res?.data?.loginResponse ?? res?.loginResponse;
  if (!refreshedInfo?.accessToken) {
    throw new Error("Refresh response missing access token");
  }

  saveSession({
    access_token: refreshedInfo.accessToken,
    refresh_token: refreshedInfo.refreshToken ?? session.refresh_token,
  });

  return refreshedInfo;
}

service.interceptors.request.use(
  (config) => {
    const session = loadSession();
    if (session?.access_token && config.headers) {
      config.headers.set("Authorization", `Bearer ${session.access_token}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

service.interceptors.response.use(
  (res) => res?.data ?? res,
  async (error) => {
    const status = error.response?.status;
    const session = loadSession();

    if (status === 403) {
      redirectToSignIn();
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    const requestUrl = error.config?.url ?? "";
    if (requestUrl.includes(apiRoutes.REFRESH_TOKEN)) {
      redirectToSignIn();
      return Promise.reject(error);
    }

    if (!session?.refresh_token) {
      redirectToSignIn();
      return Promise.reject(error);
    }

    try {
      await refreshSessionTokens();
      window.location.reload();
      return Promise.reject(error);
    } catch (refreshErr) {
      if (axios.isAxiosError(refreshErr)) {
        const rs = refreshErr.response?.status;
        if (rs === 401 || rs === 403) {
          redirectToSignIn();
        } else if (!refreshErr.response) {
          return Promise.reject(error);
        } else {
          redirectToSignIn();
        }
      } else {
        redirectToSignIn();
      }
      return Promise.reject(error);
    }
  }
);

export default service;
